import { useCallback, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import type { ApiResponseBackupList } from '../types/api/ApiResponseBackup';
import type { BackupSummary } from '../types/models/Backup';

/**
 * The endpoint's documented maximum page size. The backup inventory is filtered and paginated
 * **client-side** (CLAUDE.md "Filterable list tables", Mode A): a real tenant holds ~16–49
 * subjects and the largest production backup server carries 92 across *all* customers, so one
 * fetch covers it. A tree table's `itemCount` is its *visible* row count and changes when a node
 * expands, which server-side `limit`/`offset` cannot express.
 */
const LIST_LIMIT = 500;

/**
 * `GET /datacenters/backups` — the tenant's backup subjects, freshest first.
 *
 * `include_missing=1` is deliberate and not configurable here: the status filter defaults to
 * "All" so a customer *sees* that a backup is missing or retired rather than having it hidden by
 * a default filter.
 *
 * `count` is the server's total ignoring paging. If it ever exceeds `backups.length` the tenant
 * has passed the 500-row ceiling and the page must say so rather than silently truncate.
 */
export function useBackups() {
  const auth = useAuth();
  const apiFetch = useApiFetch();
  const [backups, setBackups] = useState<BackupSummary[]>([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchBackups = useCallback(async () => {
    if (!auth.user?.access_token) return;

    setLoading(true);
    setError(null);

    try {
      const response = await apiFetch(`/datacenters/backups?include_missing=1&limit=${LIST_LIMIT}`);
      const json = (await response.json().catch(() => null)) as ApiResponseBackupList | null;

      if (json === null) {
        throw new Error(`Unexpected response from server (HTTP ${response.status})`);
      }
      if (json.status !== 'success' || !json.data) {
        throw new Error(json.message ?? 'Unexpected response from server');
      }

      setBackups(json.data.backups ?? []);
      setCount(json.data.count ?? 0);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('An unexpected error occurred'));
    } finally {
      setLoading(false);
    }
  }, [auth.user?.access_token, apiFetch]);

  useEffect(() => {
    fetchBackups();
  }, [fetchBackups]);

  return { backups, count, loading, error, refetch: fetchBackups };
}
